/**
 * @file     PanelSkeletons.tsx
 * @purpose  Loading placeholders for the results panel at the settled height
 *           of what replaces them: the bare card tile (4:3 photo, price, specs,
 *           address, attribution, Add to list) and the hairline ListingRow.
 *           Light on imports so the /properties loading fallback can use them.
 */
import Box from '@mui/material/Box';
import { SkeletonBlock } from '@/design-system/skeletons/SkeletonBlock';
import { layoutTokens } from '@/design-system/tokens/layout';
import { hairline } from '@/components/hub/hubKitStyles';

const photo = { height: 'auto', aspectRatio: '4 / 3' } as const;

export function TileSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SkeletonBlock height={0} radius={layoutTokens.radius.lg} style={photo} />
      <SkeletonBlock width="55%" height={20} radius={layoutTokens.radius.pill} marginTop={4} />
      <SkeletonBlock width="70%" height={14} radius={layoutTokens.radius.pill} />
      <SkeletonBlock width="85%" height={14} radius={layoutTokens.radius.pill} />
      <SkeletonBlock width="50%" height={11} radius={layoutTokens.radius.pill} marginTop={8} />
      <SkeletonBlock height={44} radius={layoutTokens.radius.md} marginTop={8} />
    </Box>
  );
}

export function RowSkeleton() {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 132px) minmax(0, 1fr)', sm: '168px minmax(0, 1fr)' },
        columnGap: { xs: 3, sm: 4 },
        py: 4,
        borderBottom: hairline,
      }}
    >
      <SkeletonBlock height={0} radius={layoutTokens.radius.sm} style={photo} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <SkeletonBlock width="45%" height={20} radius={layoutTokens.radius.pill} />
        <SkeletonBlock width="60%" height={14} radius={layoutTokens.radius.pill} />
        <SkeletonBlock width="80%" height={14} radius={layoutTokens.radius.pill} />
        <SkeletonBlock width="50%" height={11} radius={layoutTokens.radius.pill} marginTop={6} />
      </Box>
    </Box>
  );
}

/** Two tiles a row in the panel, one on phones. */
export function TileSkeletonGrid({ count = 4, label }: { count?: number; label?: string }) {
  return (
    <Box
      role="status"
      aria-label={label}
      sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', columnGap: 4, rowGap: 7 }}
    >
      {Array.from({ length: count }, (_, i) => (
        <TileSkeleton key={i} />
      ))}
    </Box>
  );
}

export function RowSkeletonList({ count = 4, label }: { count?: number; label?: string }) {
  return (
    <Box role="status" aria-label={label}>
      {Array.from({ length: count }, (_, i) => (
        <RowSkeleton key={i} />
      ))}
    </Box>
  );
}
